import AddRounded from "@mui/icons-material/AddRounded";
import ManageSearchRounded from "@mui/icons-material/ManageSearchRounded";
import { Alert, Box, Paper, Skeleton, Stack, Typography } from "@mui/material";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React from "react";
import {
    KboFlowQuestionSubscription,
    KboFlowSubscription,
    KboFlowSubscriptionsService,
    RunKboFlowQuestionOnceRequest,
    SaveKboFlowQuestionSubscriptionRequest,
} from "services/KboFlowSubscriptionsService";
import { ServiceError } from "services/BaseService";
import { AuthStore } from "stores/AuthStore";
import { useStore } from "utils/useStore";
import { KboFlowDiscoverySubscriptionCard } from "./KboFlowDiscoverySubscriptionCard";
import {
    QuestionRejection,
    QuestionSubscriptionDialog,
} from "./QuestionSubscriptionDialog";
import { BannerHighlight, SubscriptionsBanner } from "./SubscriptionsBanner";

const cadenceLabel = (cadence: SubscriptionCadence) =>
    cadence === SubscriptionCadence.Monthly ? "Monthly" : "Weekly";

/** The HTTP status and user-facing message behind a failed request. */
const describeError = (error: unknown, fallback: string) => {
    if (error instanceof ServiceError) {
        const json: unknown = error.errorJson;
        const message =
            (typeof json === "string" && json) ||
            (typeof json === "object" &&
                (json as { message?: string } | null)?.message) ||
            error.errorMessage;
        return { status: error.response.status, message: message || fallback };
    }
    return { status: undefined, message: fallback };
};

const errorMessage = (error: unknown, fallback: string) =>
    describeError(error, fallback).message;

/** The status the platform returns when KBo's validation rejects a question. */
const QUESTION_REJECTED_STATUS = 422;

/**
 * "My Subscriptions" tab under KBo Settings (internal Authenticx users only). The built-in
 * KBo Flow (discovery) subscription is pinned first; below it, each question subscription is a
 * question KBo researches on a weekly or monthly cadence and emails the findings to the current
 * user. Subscriptions are per user within the selected tenant.
 */
const MySubscriptionsPage = observer(function MySubscriptionsPage() {
    const messageStore = useStore(MessageStore);
    const authStore = useStore(AuthStore);
    const service = React.useMemo(() => new KboFlowSubscriptionsService(), []);

    const [loading, setLoading] = React.useState(true);
    const [loadError, setLoadError] = React.useState<string>();
    const [subscriptions, setSubscriptions] = React.useState<
        KboFlowQuestionSubscription[]
    >([]);
    const [discoverySubscription, setDiscoverySubscription] =
        React.useState<KboFlowSubscription | null>(null);
    const [discoverySaving, setDiscoverySaving] = React.useState(false);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editing, setEditing] = React.useState<KboFlowQuestionSubscription>();
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string>();
    const [rejection, setRejection] = React.useState<QuestionRejection>();
    const [deleting, setDeleting] = React.useState(false);

    React.useEffect(() => {
        const controller = new AbortController();

        Promise.all([
            service.listMyQuestions(controller.signal),
            service.getMine(),
        ])
            .then(([questions, discovery]) => {
                if (controller.signal.aborted) return;
                setSubscriptions(questions);
                setDiscoverySubscription(discovery);
            })
            .catch((error) => {
                if (!controller.signal.aborted) {
                    setLoadError(
                        errorMessage(
                            error,
                            "Your subscriptions could not be loaded.",
                        ),
                    );
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });

        return () => controller.abort();
    }, [service]);

    const subscribeDiscovery = async (cadence: SubscriptionCadence) => {
        const wasSubscribed = Boolean(discoverySubscription);
        setDiscoverySaving(true);
        try {
            setDiscoverySubscription(await service.subscribeMine(cadence));
            messageStore.logMessage(
                wasSubscribed
                    ? `KBo Flow now sends ${cadenceLabel(
                          cadence,
                      ).toLowerCase()}`
                    : "Subscription turned on",
                "success",
            );
        } catch (error) {
            messageStore.logError(
                errorMessage(error, "The subscription could not be saved."),
            );
        } finally {
            setDiscoverySaving(false);
        }
    };

    const turnOffDiscovery = async () => {
        setDiscoverySaving(true);
        try {
            await service.unsubscribeMine();
            setDiscoverySubscription(null);
            messageStore.logMessage("Subscription turned off", "success");
        } catch (error) {
            messageStore.logError(
                errorMessage(error, "The subscription could not be updated."),
            );
        } finally {
            setDiscoverySaving(false);
        }
    };

    const openDialog = (subscription?: KboFlowQuestionSubscription) => {
        setEditing(subscription);
        setSaveError(undefined);
        setRejection(undefined);
        setDialogOpen(true);
    };

    const save = async (request: SaveKboFlowQuestionSubscriptionRequest) => {
        setSaving(true);
        setSaveError(undefined);
        setRejection(undefined);
        try {
            if (editing) {
                const updated = await service.updateQuestion(
                    editing.id,
                    request,
                );
                // A frequency change replaces the subscription, so match on the old id.
                setSubscriptions((current) =>
                    current.map((item) =>
                        item.id === editing.id ? updated : item,
                    ),
                );
                messageStore.logMessage("Subscription updated", "success");
            } else {
                const created = await service.createQuestion(request);
                setSubscriptions((current) => [created, ...current]);
                messageStore.logMessage("Subscription added", "success");
            }

            setDialogOpen(false);
        } catch (error) {
            const { status, message } = describeError(
                error,
                "The subscription could not be saved.",
            );
            if (status === QUESTION_REJECTED_STATUS) {
                setRejection({
                    questionText: request.questionText,
                    reason: describeError(error, "").message || undefined,
                });
            } else {
                setSaveError(message);
            }
        } finally {
            setSaving(false);
        }
    };

    const runOnce = async (request: RunKboFlowQuestionOnceRequest) => {
        setSaving(true);
        setSaveError(undefined);
        try {
            await service.runQuestionOnce(request);
            messageStore.logMessage("KBo Flow run started", "success");
            setDialogOpen(false);
        } catch (error) {
            setSaveError(errorMessage(error, "The run could not be started."));
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        if (!editing) return;

        setDeleting(true);
        try {
            await service.removeQuestion(editing.id);
            setSubscriptions((current) =>
                current.filter((item) => item.id !== editing.id),
            );
            messageStore.logMessage("Subscription deleted", "success");
            setDialogOpen(false);
        } catch (error) {
            setSaveError(
                errorMessage(error, "The subscription could not be deleted."),
            );
        } finally {
            setDeleting(false);
        }
    };

    const activeCount = subscriptions.length + (discoverySubscription ? 1 : 0);

    return (
        <Stack rowGap={2} padding={3} width="100%">
            <SubscriptionsBanner
                id="kbo-question-subscriptions-banner"
                title="My subscriptions"
                loading={loading}
                summary={
                    <>
                        You have{" "}
                        <BannerHighlight>
                            {`${activeCount} ${
                                activeCount === 1
                                    ? "subscription"
                                    : "subscriptions"
                            }`}
                        </BannerHighlight>{" "}
                        delivering KBo Flow research to your inbox.
                    </>
                }
                action={
                    <AcxButton
                        id="kbo-question-subscription-add"
                        color="white"
                        variant="contained"
                        startIcon={<AddRounded />}
                        onClick={() => openDialog()}
                        disabled={loading || Boolean(loadError)}
                    >
                        Add Subscription
                    </AcxButton>
                }
            />

            <Typography
                component="h2"
                sx={{
                    marginTop: 1,
                    fontSize: 18,
                    lineHeight: "26px",
                    fontWeight: 600,
                    color: "#111827",
                }}
            >
                Subscriptions
            </Typography>

            {loadError && <Alert severity="error">{loadError}</Alert>}

            {loading ? (
                <Stack rowGap={1.5}>
                    <Skeleton variant="rounded" height={140} />
                    <Skeleton variant="rounded" height={96} />
                </Stack>
            ) : (
                !loadError && (
                    <Stack rowGap={1.5}>
                        <KboFlowDiscoverySubscriptionCard
                            subscription={discoverySubscription}
                            saving={discoverySaving}
                            onSubscribe={(cadence) =>
                                void subscribeDiscovery(cadence)
                            }
                            onTurnOff={() => void turnOffDiscovery()}
                        />
                        {subscriptions.map((subscription) => (
                            <QuestionSubscriptionCard
                                key={subscription.id}
                                subscription={subscription}
                                onEdit={() => openDialog(subscription)}
                            />
                        ))}
                    </Stack>
                )
            )}

            <QuestionSubscriptionDialog
                isOpen={dialogOpen}
                subscription={editing}
                saving={saving}
                deleting={deleting}
                saveError={saveError}
                rejection={rejection}
                canRunOnce={authStore.isUserUltra()}
                onClose={() => setDialogOpen(false)}
                onSave={(request) => void save(request)}
                onRunOnce={(request) => void runOnce(request)}
                onDelete={() => void remove()}
            />
        </Stack>
    );
});

type QuestionSubscriptionCardProps = {
    subscription: KboFlowQuestionSubscription;
    onEdit: () => void;
};

function QuestionSubscriptionCard({
    subscription,
    onEdit,
}: QuestionSubscriptionCardProps) {
    return (
        <Paper
            id={`kbo-question-subscription-${subscription.id}`}
            variant="outlined"
            sx={{
                padding: "18px 20px",
                borderRadius: "10px",
                borderColor: "#D1D5DB",
            }}
        >
            <Stack
                direction="row"
                alignItems="center"
                columnGap={1.75}
                rowGap={1}
                flexWrap="wrap"
            >
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        flexShrink: 0,
                        borderRadius: "8px",
                        backgroundColor: "#F3F4F6",
                        display: "grid",
                        placeItems: "center",
                    }}
                >
                    <ManageSearchRounded
                        titleAccess="My Flow"
                        sx={{ fontSize: 22, color: "#374151" }}
                    />
                </Box>
                <Stack rowGap={0.25} flex={1} minWidth={220}>
                    <Typography
                        sx={{
                            fontSize: 14,
                            lineHeight: "20px",
                            fontWeight: 600,
                            color: "#111827",
                        }}
                    >
                        {subscription.name}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: 13,
                            lineHeight: "19px",
                            color: "#4B5563",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                        }}
                    >
                        {subscription.questionText}
                    </Typography>
                    <Typography
                        sx={{
                            marginTop: "2px",
                            fontSize: 13,
                            lineHeight: "18px",
                            color: "#6B7280",
                        }}
                    >
                        {cadenceLabel(subscription.cadence)}
                    </Typography>
                </Stack>
                <AcxButton
                    id={`kbo-question-subscription-edit-${subscription.id}`}
                    color="white"
                    variant="outlined"
                    onClick={onEdit}
                >
                    Edit
                </AcxButton>
            </Stack>
        </Paper>
    );
}

export default MySubscriptionsPage;
